import type { Plan, PlannerConfig } from "../types";
import type { CubeBelief } from "./belief";
import { scanHoldSteps } from "./trajectory";

const SEARCH_SCALE = [1, -2, -1] as const;

/** Stateful, non-cancelling blind search from the hardware v9 planner. */
export class ScanController {
  private active = false;
  private phase = 0;
  private sign = 1;
  private rung = -1;

  constructor(private readonly cfg: PlannerConfig) {}

  reset(): void {
    this.active = false;
    this.phase = 0;
    this.sign = 1;
    this.rung = -1;
  }

  shape(raw: Plan, belief: CubeBelief, rung: number): Plan {
    if (raw.mode !== "scan" || raw.approachTurn) {
      if (raw.mode === "clip" || raw.mode === "approach" || raw.approachTurn) this.reset();
      return raw.mode === "scan"
        ? { ...raw, hold: scanHoldSteps(raw.yawOffset, this.cfg) }
        : raw;
    }
    if (!this.cfg.stateful_scan) {
      return { ...raw, hold: scanHoldSteps(raw.yawOffset, this.cfg) };
    }
    // A colour-bearing partial observation keeps its directed, shorter reframe.
    if (belief.valid) {
      this.reset();
      return { ...raw, hold: scanHoldSteps(raw.yawOffset, this.cfg) };
    }
    if (!this.active || this.rung !== rung) {
      this.active = true;
      this.phase = 0;
      this.rung = rung;
      this.sign = this.cfg.search_left_first ? 1 : (raw.yawOffset < 0 ? -1 : 1);
    }
    const sweep = this.cfg.scan_sweep_deg * Math.PI / 180;
    const yawOffset = this.sign * (SEARCH_SCALE[this.phase] ?? 1) * sweep;
    return {
      ...raw,
      yawOffset,
      hold: scanHoldSteps(yawOffset, this.cfg),
      label: `search ${this.phase + 1}/3`,
      searchScan: true,
      searchPhase: this.phase,
    };
  }

  commit(plan: Plan): void {
    if (!plan.searchScan) return;
    this.phase = plan.searchPhase + 1;
    if (this.phase >= SEARCH_SCALE.length) {
      this.phase = 0;
      this.sign = this.cfg.search_left_each_cycle ? 1 : -this.sign;
    }
  }
}
